import React, { useState, useEffect } from 'react';
import { obtenerConfiguracion, guardarConfiguracion } from '../../services/configTiendaApi';
import { obtenerEstilosAdmin, guardarEstilos, ESTILOS_DEFAULT } from '../../services/themeService';
import { resolveImagenUrl } from '../../config/apiConfig';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { BANNERS_DISPONIBLES, PLANTILLAS_CARTA } from './tiendaConstants';
import { VistaPreviaMovil } from './tiendaSubComponents';
import './ConfiguradorTienda.css';

function ShopifyCard({ icono, titulo, badge, children }) {
    return (
        <div className="shopify-card">
            <div className="shopify-card-header">
                <div className="shopify-card-icon"><i className={icono}></i></div>
                <h6 className="shopify-card-title">{titulo}</h6>
                {badge && <span className="config-badge public">{badge}</span>}
            </div>
            {children}
        </div>
    );
}

function ToggleRow({ label, desc, checked, onChange }) {
    return (
        <div className={`toggle-row ${checked ? 'active' : ''}`}>
            <div>
                <div className="toggle-row-label">{label}</div>
                <div className="toggle-row-desc">{desc}</div>
            </div>
            <label className="config-toggle">
                <input type="checkbox" checked={checked} onChange={onChange} />
                <span className="config-toggle-slider"></span>
            </label>
        </div>
    );
}

function ConfiguradorTienda() {
    const [config, setConfig] = useState({
        nombre: '', urlSlug: '', colorPrimario: '#F26B21',
        horario: '', direccion: '', descripcion: '',
        urlCabecera: null, urlLogo: '',
        datosTransferencia: null
    });
    const [estilos, setEstilos] = useState(ESTILOS_DEFAULT);
    const [plantillaActiva, setPlantillaActiva] = useState(null);
    const [loading, setLoading] = useState(false);
    const [cambiosPendientes, setCambiosPendientes] = useState(false);

    const urlQrCompleta = config.urlSlug ? `${window.location.origin}/qr/${config.urlSlug}` : '';
    const mostrarPrecios = estilos.carta_qr ? estilos.carta_qr.mostrar_precios !== false : true;

    useEffect(() => {
        const cargar = async () => {
            try {
                const res = await obtenerConfiguracion();
                if (res) {
                    setConfig({
                        nombre: res.nombre || '',
                        urlSlug: res.url_slug || res.urlSlug || '',
                        colorPrimario: res.color_primario || res.colorPrimario || '#F26B21',
                        horario: res.horario || '',
                        direccion: res.direccion || '',
                        descripcion: res.descripcion || '',
                        urlCabecera: resolveImagenUrl(res.url_cabecera || res.urlCabecera) || null,
                        urlLogo: resolveImagenUrl(res.url_logo || res.urlLogo) || '',
                        datosTransferencia: res.datosTransferencia || res.datos_transferencia || null
                    });
                }
            } catch (err) {}
        };
        cargar();
    }, []);

    useEffect(() => {
        const cargarEst = async () => {
            const data = await obtenerEstilosAdmin();
            setEstilos(data);
            if (data.carta_qr && data.carta_qr.plantilla_id) {
                setPlantillaActiva(data.carta_qr.plantilla_id);
            }
        };
        cargarEst();
    }, []);

    const handleChange = (e) => {
        setConfig({ ...config, [e.target.name]: e.target.value });
        setCambiosPendientes(true);
    };

    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onloadend = () => {
            setConfig(prev => ({ ...prev, urlLogo: reader.result }));
            setCambiosPendientes(true);
        };
        reader.readAsDataURL(file);
    };

    const handleHeaderChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onloadend = () => {
            setConfig(prev => ({ ...prev, urlCabecera: reader.result }));
            setCambiosPendientes(true);
        };
        reader.readAsDataURL(file);
    };

    const aplicarPlantilla = (plantilla) => {
        setPlantillaActiva(plantilla.id);
        setEstilos(prev => ({
            ...prev,
            carta_qr: { ...(prev.carta_qr || {}), ...plantilla.carta_qr, plantilla_id: plantilla.id }
        }));
        setCambiosPendientes(true);
    };

    const handleGuardar = async () => {
        setLoading(true);
        try {
            const formData = new FormData();
            formData.append('nombre', config.nombre || '');
            formData.append('urlSlug', config.urlSlug || '');
            formData.append('colorPrimario', config.colorPrimario || '');
            formData.append('horario', config.horario || '');
            formData.append('direccion', config.direccion || '');
            formData.append('descripcion', config.descripcion || '');
            formData.append('urlCabecera', config.urlCabecera || '');
            formData.append('urlLogo', config.urlLogo || '');
            if (config.datosTransferencia) {
                formData.append('datosTransferencia', JSON.stringify(config.datosTransferencia));
            }
            const logoInput = document.getElementById('logoInput');
            if (logoInput && logoInput.files[0]) formData.append('logo_file', logoInput.files[0]);

            const [resConfig, resEstilosResult] = await Promise.allSettled([
                guardarConfiguracion(formData),
                guardarEstilos(estilos)
            ]);

            const estilosOk = resEstilosResult.status === 'fulfilled' &&
                resEstilosResult.value &&
                (resEstilosResult.value.status === 'ok' || resEstilosResult.value.status === 'success');
            const configOk = resConfig.status === 'fulfilled';
            const mensajeErrorConfig = resConfig.status === 'rejected'
                ? ((resConfig.reason && resConfig.reason.response && resConfig.reason.response.data && resConfig.reason.response.data.error) || 'Error al guardar datos de tienda')
                : null;

            if (estilosOk && configOk) {
                toast.success('Cambios guardados');
                setCambiosPendientes(false);
            } else if (configOk) {
                toast.success('Datos guardados (error al guardar diseño)');
                setCambiosPendientes(false);
            } else {
                toast.error(mensajeErrorConfig || 'Error al guardar');
            }
        } catch {
            toast.error('Error al guardar');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="config-tienda">
            <ToastContainer position="top-right" autoClose={2500} hideProgressBar />

            <div className="config-header">
                <div className="config-header-left">
                    <div className="config-header-icon"><i className="bi bi-qr-code"></i></div>
                    <div>
                        <h1 className="config-header-title">Carta QR</h1>
                        <p className="config-header-subtitle">Configura tu carta digital y su apariencia.</p>
                    </div>
                </div>
                <div className="config-header-right">
                    {cambiosPendientes && (
                        <span className="config-unsaved-badge">
                            <i className="bi bi-exclamation-circle"></i> Sin guardar
                        </span>
                    )}
                    <button className="config-btn-save" onClick={handleGuardar} disabled={loading}>
                        {loading
                            ? <span className="spinner-border spinner-border-sm" />
                            : <i className="bi bi-check-lg"></i>}
                        Guardar
                    </button>
                </div>
            </div>

            <div className="config-body config-body-flat">
                <div className="config-content">
                    <div className="config-content-inner">
                        <div className="row g-4">

                            {/* COLUMNA IZQUIERDA */}
                            <div className="col-lg-7">

                                {/* LOGO */}
                                <ShopifyCard icono="bi bi-image" titulo="Logo">
                                    <input type="file" accept="image/*" id="logoInput"
                                        style={{ display: 'none' }} onChange={handleLogoChange} />
                                    <label htmlFor="logoInput" style={{ display: 'block', cursor: 'pointer' }}>
                                        <div style={{
                                            width: '100%', height: '120px', borderRadius: '12px',
                                            border: '2px dashed #e2e8f0', display: 'flex',
                                            alignItems: 'center', justifyContent: 'center', gap: '20px',
                                            background: '#fafafa', transition: 'border-color 0.2s', overflow: 'hidden'
                                        }}>
                                            {config.urlLogo ? (
                                                <>
                                                    <img src={config.urlLogo} alt="Logo"
                                                        style={{ height: '88px', width: '88px', objectFit: 'cover', borderRadius: '12px', border: '2px solid #e2e8f0' }} />
                                                    <div>
                                                        <div style={{ fontWeight: 700, fontSize: '13px', color: '#374151' }}>Cambiar logo</div>
                                                        <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>Cuadrado 500×500 px</div>
                                                    </div>
                                                </>
                                            ) : (
                                                <div style={{ textAlign: 'center' }}>
                                                    <i className="bi bi-cloud-upload" style={{ fontSize: '30px', color: '#d1d5db' }}></i>
                                                    <div style={{ fontWeight: 700, fontSize: '13px', color: '#6b7280', marginTop: '6px' }}>Subir logo</div>
                                                    <div style={{ fontSize: '11px', color: '#9ca3af' }}>Cuadrado 500×500 px</div>
                                                </div>
                                            )}
                                        </div>
                                    </label>

                                    <div style={{ marginTop: '14px' }}>
                                        <div style={{ fontSize: '11px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' }}>
                                            Tamaño del logo
                                        </div>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            {[
                                                { val: 'pequeño', label: 'Pequeño', size: 32 },
                                                { val: 'mediano', label: 'Mediano', size: 48 },
                                                { val: 'grande', label: 'Grande', size: 64 },
                                            ].map(opt => {
                                                const activo = ((estilos.carta_qr && estilos.carta_qr.logo_tamano) || 'mediano') === opt.val;
                                                return (
                                                    <button key={opt.val}
                                                        onClick={() => {
                                                            setEstilos(prev => ({ ...prev, carta_qr: { ...(prev.carta_qr || {}), logo_tamano: opt.val } }));
                                                            setCambiosPendientes(true);
                                                        }}
                                                        style={{
                                                            flex: 1, padding: '10px 4px', borderRadius: '10px',
                                                            border: `2px solid ${activo ? '#008060' : '#e2e8f0'}`,
                                                            background: activo ? '#f0fdf4' : '#fff',
                                                            color: activo ? '#008060' : '#6b7280',
                                                            fontWeight: 600, fontSize: '12px', cursor: 'pointer',
                                                            display: 'flex', flexDirection: 'column',
                                                            alignItems: 'center', gap: '6px', transition: 'all 0.15s'
                                                        }}>
                                                        <div style={{
                                                            width: `${opt.size}px`, height: `${opt.size}px`,
                                                            borderRadius: '50%', background: activo ? '#008060' : '#e2e8f0',
                                                            transition: 'all 0.15s'
                                                        }} />
                                                        {opt.label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </ShopifyCard>

                                {/* PORTADA */}
                                <ShopifyCard icono="bi bi-images" titulo="Portada">
                                    {/* Upload propio */}
                                    <input type="file" accept="image/*" id="headerInput"
                                        style={{ display: 'none' }} onChange={handleHeaderChange} />
                                    <label htmlFor="headerInput" style={{ display: 'block', cursor: 'pointer', marginBottom: '14px' }}>
                                        <div style={{
                                            width: '100%', height: '90px', borderRadius: '10px',
                                            border: '2px dashed #e2e8f0', overflow: 'hidden',
                                            position: 'relative', background: '#fafafa',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '14px'
                                        }}>
                                            {config.urlCabecera ? (
                                                <>
                                                    <img src={config.urlCabecera} alt="Header"
                                                        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                                                    <div style={{
                                                        position: 'absolute', inset: 0,
                                                        background: 'rgba(0,0,0,0.35)', display: 'flex',
                                                        alignItems: 'center', justifyContent: 'center', gap: '8px'
                                                    }}>
                                                        <i className="bi bi-pencil" style={{ color: '#fff', fontSize: '16px' }}></i>
                                                        <span style={{ color: '#fff', fontWeight: 700, fontSize: '13px' }}>Cambiar imagen</span>
                                                    </div>
                                                </>
                                            ) : (
                                                <div style={{ textAlign: 'center' }}>
                                                    <i className="bi bi-cloud-upload" style={{ fontSize: '26px', color: '#d1d5db' }}></i>
                                                    <div style={{ fontWeight: 700, fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>Subir imagen propia</div>
                                                    <div style={{ fontSize: '11px', color: '#9ca3af' }}>1200×300 px recomendado</div>
                                                </div>
                                            )}
                                        </div>
                                    </label>

                                    {/* Presets */}
                                    <div style={{ fontSize: '11px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' }}>
                                        O elige una foto de muestra
                                    </div>
                                    <div className="d-flex flex-wrap gap-2">
                                        {BANNERS_DISPONIBLES.map(banner => {
                                            const esSel = config.urlCabecera === banner.url;
                                            return (
                                                <div key={banner.id}
                                                    onClick={() => { setConfig(prev => ({ ...prev, urlCabecera: banner.url })); setCambiosPendientes(true); }}
                                                    style={{
                                                        width: '100px', cursor: 'pointer', borderRadius: '10px',
                                                        overflow: 'hidden', border: `2px solid ${esSel ? '#008060' : '#e2e8f0'}`,
                                                        boxShadow: esSel ? '0 0 0 3px rgba(0,128,96,0.15)' : 'none',
                                                        transition: 'all 0.2s', position: 'relative'
                                                    }}>
                                                    {esSel && (
                                                        <div style={{
                                                            position: 'absolute', top: 4, right: 4,
                                                            width: '18px', height: '18px', borderRadius: '50%',
                                                            background: '#008060', display: 'flex',
                                                            alignItems: 'center', justifyContent: 'center', zIndex: 2
                                                        }}>
                                                            <i className="bi bi-check" style={{ color: '#fff', fontSize: '10px' }}></i>
                                                        </div>
                                                    )}
                                                    {banner.url ? (
                                                        <img src={banner.url} alt={banner.nombre}
                                                            style={{ width: '100%', height: '52px', objectFit: 'cover' }} />
                                                    ) : (
                                                        <div style={{ width: '100%', height: '52px', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            <i className="bi bi-slash-circle" style={{ color: '#ccc', fontSize: '18px' }}></i>
                                                        </div>
                                                    )}
                                                    <div style={{ fontSize: '10px', fontWeight: 600, textAlign: 'center', padding: '4px', color: esSel ? '#008060' : '#6b7280', background: '#fff' }}>
                                                        {banner.nombre}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </ShopifyCard>

                                {/* INFORMACIÓN */}
                                <ShopifyCard icono="bi bi-info-circle" titulo="Información">
                                    <div className="mb-3">
                                        <label className="config-label">Nombre del negocio</label>
                                        <input type="text" name="nombre" className="config-input"
                                            value={config.nombre} onChange={handleChange}
                                            placeholder="Ej: Barra Valdín" />
                                    </div>
                                    <div className="config-grid-2 mb-3">
                                        <div>
                                            <label className="config-label">Dirección</label>
                                            <input type="text" name="direccion" className="config-input"
                                                value={config.direccion} onChange={handleChange}
                                                placeholder="Ej: Av. Principal 123" />
                                        </div>
                                        <div>
                                            <label className="config-label">Horario</label>
                                            <input type="text" name="horario" className="config-input"
                                                value={config.horario} onChange={handleChange}
                                                placeholder="Ej: Lun–Vie 12:00–22:00" />
                                        </div>
                                    </div>
                                    <div className="mb-3">
                                        <label className="config-label">Descripción</label>
                                        <textarea name="descripcion" className="config-textarea" rows="2"
                                            value={config.descripcion} onChange={handleChange}
                                            placeholder="Una línea sobre tu negocio..." />
                                    </div>
                                    <div>
                                        <label className="config-label">URL de tu carta</label>
                                        <div className="input-group">
                                            <span className="input-group-text bg-light text-muted small">
                                                {window.location.origin.replace(/^https?:\/\//, '')}/qr/
                                            </span>
                                            <input type="text" name="urlSlug"
                                                className="form-control fw-bold text-primary"
                                                value={config.urlSlug}
                                                onChange={(e) => {
                                                    const val = e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
                                                    setConfig(prev => ({ ...prev, urlSlug: val }));
                                                    setCambiosPendientes(true);
                                                }}
                                                placeholder="mi-negocio"
                                                style={{ borderRadius: '8px', border: '1px solid #e2e8f0', padding: '10px 14px' }} />
                                        </div>
                                    </div>
                                </ShopifyCard>

                                {/* DISEÑO — plantillas */}
                                <ShopifyCard icono="bi bi-palette2" titulo="Diseño">
                                    <p className="text-muted mb-3" style={{ fontSize: '13px' }}>
                                        Elige el estilo de tu carta. Aplica colores e identidad visual de un click.
                                    </p>
                                    <div className="row g-3">
                                        {PLANTILLAS_CARTA.map(p => {
                                            const activa = plantillaActiva === p.id;
                                            return (
                                                <div key={p.id} className="col-6 col-md-4">
                                                    <div onClick={() => aplicarPlantilla(p)}
                                                        style={{
                                                            cursor: 'pointer', borderRadius: '12px',
                                                            overflow: 'hidden',
                                                            border: `2px solid ${activa ? '#008060' : '#e2e8f0'}`,
                                                            boxShadow: activa ? '0 0 0 3px rgba(0,128,96,0.15)' : 'none',
                                                            transition: 'all 0.2s'
                                                        }}>
                                                        {/* Header preview */}
                                                        <div style={{
                                                            height: '52px', background: p.preview.bg,
                                                            display: 'flex', alignItems: 'center',
                                                            justifyContent: 'center', position: 'relative'
                                                        }}>
                                                            <div style={{
                                                                width: '28px', height: '28px', borderRadius: '50%',
                                                                background: 'rgba(255,255,255,0.85)'
                                                            }} />
                                                            {activa && (
                                                                <div style={{
                                                                    position: 'absolute', top: 6, right: 6,
                                                                    width: '18px', height: '18px', background: '#008060',
                                                                    borderRadius: '50%', display: 'flex',
                                                                    alignItems: 'center', justifyContent: 'center'
                                                                }}>
                                                                    <i className="bi bi-check" style={{ color: '#fff', fontSize: '10px' }}></i>
                                                                </div>
                                                            )}
                                                        </div>
                                                        {/* Mini product grid */}
                                                        <div style={{
                                                            background: p.carta_qr.color_fondo || '#f8f9fa',
                                                            padding: '6px 8px',
                                                            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px'
                                                        }}>
                                                            {[0, 1].map(i => (
                                                                <div key={i} style={{
                                                                    background: '#fff',
                                                                    borderRadius: p.carta_qr.tarjeta_radio === 'cuadrado' ? '4px' : '8px',
                                                                    height: '24px',
                                                                    boxShadow: p.carta_qr.tarjeta_sombra ? '0 1px 4px rgba(0,0,0,0.12)' : 'none'
                                                                }} />
                                                            ))}
                                                        </div>
                                                        {/* Footer preview */}
                                                        <div style={{
                                                            height: '18px', background: p.preview.footer,
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                                                        }}>
                                                            <div style={{
                                                                width: '38px', height: '3px',
                                                                background: p.preview.text,
                                                                opacity: 0.35, borderRadius: '2px'
                                                            }} />
                                                        </div>
                                                        {/* Nombre */}
                                                        <div style={{
                                                            padding: '8px', background: '#fff',
                                                            borderTop: '1px solid #f3f4f6', textAlign: 'center'
                                                        }}>
                                                            <div style={{ fontSize: '11px', fontWeight: 700, color: '#1a1c1d' }}>{p.nombre}</div>
                                                            <div style={{ fontSize: '10px', color: '#9ca3af' }}>{p.descripcion}</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </ShopifyCard>

                                {/* QR */}
                                <ShopifyCard icono="bi bi-qr-code" titulo="Código QR" badge="Público">
                                    {config.urlSlug ? (
                                        <div style={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
                                            <img
                                                src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(urlQrCompleta)}`}
                                                alt="QR"
                                                style={{ width: '100px', height: '100px', borderRadius: '10px', border: '1px solid #e2e8f0', flexShrink: 0 }}
                                            />
                                            <div style={{ flex: 1 }}>
                                                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Enlace de tu carta:</div>
                                                <div style={{ fontSize: '13px', fontWeight: 700, color: '#008060', marginBottom: '14px', wordBreak: 'break-all' }}>
                                                    {urlQrCompleta.replace(/^https?:\/\//, '')}
                                                </div>
                                                <button
                                                    className="btn btn-dark btn-sm rounded-pill fw-bold px-3"
                                                    onClick={() => {
                                                        const a = document.createElement('a');
                                                        a.href = `https://api.qrserver.com/v1/create-qr-code/?size=500x500&data=${encodeURIComponent(urlQrCompleta)}`;
                                                        a.download = `qr-${config.urlSlug}.png`;
                                                        a.target = '_blank';
                                                        a.click();
                                                    }}>
                                                    <i className="bi bi-download me-1"></i> Descargar QR
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <p className="text-muted small mb-0">
                                            Ingresa la URL en "Información" para generar el QR.
                                        </p>
                                    )}
                                    {/* Misma preferencia que la tienda, pero del bloque carta_qr: el
                                        mismo local puede tener lista en el QR y cuadricula en la tienda. */}
                                    <div style={{ marginTop: '16px', borderTop: '1px solid #f3f4f6', paddingTop: '16px' }}>
                                        <div style={{ fontSize: '11px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' }}>
                                            Vista del catálogo
                                        </div>
                                        <div style={{ display: 'flex', gap: '10px' }}>
                                            {[
                                                { val: 'grilla', label: 'Cuadricula', desc: 'Todos los productos de una' },
                                                { val: 'categorias', label: 'Lista de categorias', desc: 'Elige categoria y entra' },
                                            ].map(opt => {
                                                const activo = ((estilos.carta_qr && estilos.carta_qr.vista_catalogo) || 'grilla') === opt.val;
                                                return (
                                                    <button key={opt.val} onClick={() => {
                                                                        setEstilos(prev => ({ ...prev, carta_qr: { ...(prev.carta_qr || {}), vista_catalogo: opt.val } }));
                                                                        setCambiosPendientes(true);
                                                                    }}
                                                        style={{
                                                            flex: 1, padding: '12px', borderRadius: '10px', textAlign: 'left', cursor: 'pointer',
                                                            border: `2px solid ${activo ? '#008060' : '#e2e8f0'}`,
                                                            background: activo ? '#f0fdf4' : '#fff'
                                                        }}>
                                                        <div style={{ marginBottom: '10px' }}>
                                                            {opt.val === 'grilla' ? (
                                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3px', width: '46px' }}>
                                                                    {[0, 1, 2, 3].map(i => <div key={i} style={{ height: '16px', borderRadius: '3px', background: activo ? '#008060' : '#e2e8f0' }} />)}
                                                                </div>
                                                            ) : (
                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', width: '46px' }}>
                                                                    {[0, 1, 2].map(i => <div key={i} style={{ height: '9px', borderRadius: '3px', background: activo ? '#008060' : '#e2e8f0' }} />)}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div style={{ fontWeight: 600, fontSize: '13px', color: activo ? '#008060' : '#374151' }}>{opt.label}</div>
                                                        <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>{opt.desc}</div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    <div style={{ marginTop: '16px', borderTop: '1px solid #f3f4f6', paddingTop: '16px' }}>
                                        <ToggleRow
                                            label="Mostrar precios"
                                            desc="Si desactivas, los clientes solo verán los nombres"
                                            checked={mostrarPrecios}
                                            onChange={(e) => {
                                                setEstilos(prev => ({
                                                    ...prev,
                                                    carta_qr: { ...(prev.carta_qr || {}), mostrar_precios: e.target.checked }
                                                }));
                                                setCambiosPendientes(true);
                                            }}
                                        />
                                    </div>
                                </ShopifyCard>

                            </div>

                            {/* COLUMNA DERECHA: preview sticky */}
                            <div className="col-lg-5">
                                <div className="sticky-card">
                                    <div className="sticky-card-header">
                                        <h6 className="fw-bold m-0 d-flex align-items-center gap-2">
                                            <i className="bi bi-phone" style={{ color: '#008060' }}></i>
                                            Vista previa
                                        </h6>
                                        <span className="config-badge">En vivo</span>
                                    </div>
                                    <div className="sticky-card-body">
                                        <VistaPreviaMovil config={config} estilos={estilos} />
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ConfiguradorTienda;
